/**
 * The lights of a scene as the fitter sees them, and Godot's two lists of its directional lights:
 * the lights the renderer draws, and the shadowed lights that share the directional shadow atlas
 * (`renderer_scene_cull.cpp:3249-3282`).
 */
import { DIRECTIONAL_SHADOW_SIZE_DEFAULT, directionalLightsDrawn, directionalShadowLightRect, } from '../../godot/rendering/lights/directionalShadow.js';
import { readDirectionalShadowDeclaration } from './declaration.js';
import { isLight } from '../isLight.js';
/**
 * Every light in the scene, in the pre-order three's `projectObject` visits them (r187
 * `WebGLRenderer.js:1873-1897`), each marked by whether the render reaches it.
 */
export function sceneLights(scene) {
    const lights = [];
    collectLights(scene, true, lights);
    return lights;
}
function collectLights(object, isParentVisible, lights) {
    const isVisible = isParentVisible && object.visible;
    if (isLight(object))
        lights.push({ light: object, isVisible });
    for (const child of object.children)
        collectLights(child, isVisible, lights);
}
/** The lights of one kind among `lights` that made a declaration, hidden ones included. */
export function declaredLights(lights, isKind, read) {
    const declared = [];
    for (const { light, isVisible } of lights) {
        if (!isKind(light))
            continue;
        const declaration = read(light);
        if (declaration)
            declared.push({ light, declaration, isVisible });
    }
    return declared;
}
/** The declared directional lights among `lights`, hidden ones included. */
export function declaredDirectionalLights(lights) {
    return declaredLights(lights, isDirectionalLight, readDirectionalShadowDeclaration);
}
function isDirectionalLight(light) {
    return light.isDirectionalLight === true;
}
/**
 * Godot's lists, from the visible declared lights in pre-order. Godot also skips a light on a layer
 * the camera does not see (`renderer_scene_cull.cpp:3258`). The previewer maps neither `cull_mask`
 * nor `layers` to three, so the lists ignore layers. Lists per camera would cost two cameras that
 * differ a sun attached and released, its map reallocated and every program switched on each render.
 */
export function directionalLightLists(declared) {
    const drawn = directionalLightsDrawn(declared.filter(({ isVisible }) => isVisible));
    // `renderer_scene_cull.cpp:3257-3277`: the drawn lights that share the atlas take their shares
    // in drawn order.
    const shadowed = drawn.filter(({ declaration }) => declaration.sharesAtlas);
    const shares = new Map();
    shadowed.forEach(({ light }, index) => {
        shares.set(light, directionalShadowLightRect(DIRECTIONAL_SHADOW_SIZE_DEFAULT, shadowed.length, index));
    });
    return { drawn: new Set(drawn.map(({ light }) => light)), first: drawn[0]?.light ?? null, shares };
}
//# sourceMappingURL=lightLists.js.map